import { useDisplaySettings } from '../auth/display-settings';
// Header menus (mockup menuButton / renderUserMenu / renderBell, elements 037 · 074): open/close on the button,
// ArrowDown opens and focuses the first (or checked) item, ↑↓ Home End move, Esc closes back to the button, Tab or a
// press outside closes. The account menu has no "역할 바꿔 보기" (mockup only).
import { SOURCE_OF_TOOL, TOOLS, type NotificationKind, type Tool } from '@aiax-hub/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type ReactNode, type RefObject } from 'react';
import { Link, useNavigate } from 'react-router';
import { logout } from '../api/auth';
import { appPath, useMarkNotifications, useNotifications } from '../api/shell';
import type { AppNotification } from '../api/types';
import { useAuth, useMyRole } from '../auth/AuthContext';
import { useFmt, useHasT, useT, type TFn } from '../i18n';
import { reduceMotion } from '../lib/media';
import { cls } from '../lib/util';
import { useViewStore } from '../state/store';
import type { Lang, Theme } from '../state/url';
import { RoleBadge } from '../ui/Badges';
import { Icon, type IconName } from '../ui/Icon';
import { announce, closeFloating, registerFloating, toast } from '../ui/layers';
import { sourceInfo } from '../ui/sources';

const ITEM_SEL = '[role="menuitem"], [role="menuitemradio"], .menu__item';

/** Menu button behaviour shared by the account and bell menus. */
export function useMenuButton(menuRef: RefObject<HTMLElement | null>, btnRef: RefObject<HTMLElement | null>) {
  const [open, setOpen] = useState(false);
  const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEM_SEL) ?? []).filter((x) => !(x as HTMLButtonElement).disabled);
  const close = (focusBtn: boolean) => {
    setOpen(false);
    if (focusBtn) btnRef.current?.focus();
  };
  const show = (focusFirst: boolean) => {
    closeFloating();
    setOpen(true);
    if (focusFirst)
      requestAnimationFrame(() => {
        const it = items();
        const target = menuRef.current?.querySelector<HTMLElement>('[aria-checked="true"]') || it[0] || menuRef.current?.querySelector<HTMLElement>('a[href], button');
        target?.focus();
      });
  };
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const tm = setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0);
    const unregister = registerFloating(() => setOpen(false));
    return () => {
      clearTimeout(tm);
      document.removeEventListener('pointerdown', onDown, true);
      unregister();
    };
  }, [open, menuRef, btnRef]);
  const buttonProps = {
    'aria-expanded': open,
    onClick: () => (open ? close(false) : show(true)),
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.key === 'ArrowDown' && !open) {
        e.preventDefault();
        show(true);
      }
    },
  };
  const menuProps = {
    hidden: !open,
    onKeyDown: (e: ReactKeyboardEvent) => {
      const it = items();
      const i = it.indexOf(document.activeElement as HTMLElement);
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        (it[(i + 1) % it.length] || it[0])?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (it[(i - 1 + it.length) % it.length] || it[0])?.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        it[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        it[it.length - 1]?.focus();
      } else if (e.key === 'Tab') close(false);
    },
    onClick: (e: ReactMouseEvent) => {
      if ((e.target as Element).closest('[role="menuitem"], [role="menuitemradio"], a.menu__item')) close(false);
    },
  };
  return { open, setOpen, close, buttonProps, menuProps };
}

function RadioItem({ checked, onPick, children }: { checked: boolean; onPick: () => void; children: ReactNode }) {
  return (
    <button className="menu__item" type="button" role="menuitemradio" aria-checked={checked} onClick={onPick}>
      <span className="menu__check" aria-hidden="true">
        <Icon name="check" />
      </span>
      {children}
    </button>
  );
}

/** Account chip + menu: my records & settings · theme · language · help · sign out. */
export function UserMenu({ onOpenHelp }: { onOpenHelp: () => void }) {
  const t = useT();
  const { me } = useAuth();
  const role = useMyRole();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useViewStore((s) => s.theme);
  const lang = useViewStore((s) => s.lang);
  const setDisplay = useDisplaySettings();
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mb = useMenuButton(menuRef, btnRef);
  if (!me) return null;
  const u = me.user;
  const roleName = role.name;
  const teamName = me.teams[0]?.name ?? null;
  const initial = (u.displayName || u.loginId || '?').trim().charAt(0).toUpperCase();
  const signOut = async () => {
    try {
      await logout();
    } catch {
      /* the session may already be gone: continue to the sign-in screen */
    }
    qc.setQueryData(['auth', 'me'], null);
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
    toast(t('login.loggedOut'), 'ok');
    navigate('/login', { replace: true });
  };
  return (
    <div className="hdr-pop">
      <button
        ref={btnRef}
        className="user-chip"
        type="button"
        aria-haspopup="menu"
        aria-controls="user-menu"
        aria-label={`${t('header.user')}: ${u.displayName} · ${roleName}`}
        {...mb.buttonProps}
      >
        <span className="user-chip__avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="user-chip__name">{u.displayName}</span>
        <RoleBadge role={role.id} name={roleName} />
        <Icon name="chevD" className="user-chip__chev" />
      </button>
      <div ref={menuRef} className="menu user-menu" id="user-menu" role="menu" aria-labelledby="mn-head-name" {...mb.menuProps}>
        <div className="menu__head" role="none">
          <b id="mn-head-name">{u.displayName}</b>
          <span>{[teamName, u.title, roleName].filter(Boolean).join(' · ')}</span>
        </div>
        <Link className="menu__item" role="menuitem" to="/me">
          <Icon name="user" />
          {t('menu.settings')}
        </Link>
        <div role="group" aria-labelledby="mn-theme" className="menu__group">
          <p className="menu__label" id="mn-theme" role="none">
            <Icon name="moon" />
            {t('menu.theme')}
          </p>
          <div className="menu__inline" role="none">
            {(['light', 'dark', 'system'] as Theme[]).map((th) => (
              <RadioItem key={th} checked={theme === th} onPick={() => setDisplay({ theme: th })}>
                {t('theme.' + th)}
              </RadioItem>
            ))}
          </div>
        </div>
        <div role="group" aria-labelledby="mn-lang" className="menu__group">
          <p className="menu__label" id="mn-lang" role="none">
            <Icon name="globe" />
            {t('menu.lang')}
          </p>
          <div className="menu__inline" role="none">
            {(['ko', 'en'] as Lang[]).map((lg) => (
              <RadioItem key={lg} checked={lang === lg} onPick={() => setDisplay({ lang: lg })}>
                <span lang={lg}>{t('header.lang.' + lg)}</span>
              </RadioItem>
            ))}
          </div>
        </div>
        <button className="menu__item" type="button" role="menuitem" onClick={() => setTimeout(onOpenHelp, 0)}>
          <Icon name="help" />
          {t('help.title')}
        </button>
        <button className="menu__item menu__item--sep" type="button" role="menuitem" onClick={() => void signOut()}>
          <Icon name="logout" />
          {t('menu.logout')}
        </button>
      </div>
    </div>
  );
}

const NOTIF_ICON: Record<NotificationKind, IconName> = {
  device_down: 'alert',
  collection_gap: 'pulse',
  collection_unknown: 'pulse',
  reconsent: 'flag',
  record_viewed: 'eye',
  token_expiring: 'key',
  budget_warning: 'coins',
  version_checking: 'refresh',
  bulk_view: 'eye',
  link_broken: 'link',
  usage_spike: 'spark',
  team_update: 'users',
  policy_changed: 'shield',
  deletion_processed: 'shield',
  approval_pending: 'shield',
  grant_expiring: 'key',
  grant_dormant: 'key',
  grant_expiry_deferred: 'key',
  audit_summary: 'scroll',
};
const BELL_RING_MS = 600;

/**
 * More specific wording when the server sent the parameters it needs (contracts NotificationItem.params: numbers and
 * keys only). Otherwise the kind's general sentence (`notif.<kind>.title` / `.body`).
 */
const NOTIF_VARIANTS: Readonly<Record<string, ReadonlyArray<{ key: string; needs: readonly string[] }>>> = {
  'notif.device_down.title': [
    { key: 'notif.device_down.titleToolDays', needs: ['tool', 'days'] },
    { key: 'notif.device_down.titleTool', needs: ['tool'] },
  ],
  'notif.collection_gap.title': [{ key: 'notif.collection_gap.titleMin', needs: ['minutes'] }],
  'notif.reconsent.title': [{ key: 'notif.reconsent.titleVersion', needs: ['version'] }],
  'notif.record_viewed.body': [{ key: 'notif.record_viewed.bodyCat', needs: ['reasonCategory'] }],
  'notif.token_expiring.title': [{ key: 'notif.token_expiring.titleDays', needs: ['days'] }],
  'notif.version_checking.title': [{ key: 'notif.version_checking.titleVersion', needs: ['tool', 'version'] }],
  'notif.bulk_view.title': [{ key: 'notif.bulk_view.titleCount', needs: ['count', 'minutes'] }],
  'notif.link_broken.title': [{ key: 'notif.link_broken.titleCount', needs: ['count'] }],
  'notif.deletion_processed.title': [{ key: 'notif.deletion_processed.titleCount', needs: ['count'] }],
  'notif.approval_pending.title': [{ key: 'notif.approval_pending.titleCount', needs: ['count'] }],
  'notif.grant_expiring.title': [{ key: 'notif.grant_expiring.titleDays', needs: ['days'] }],
  'notif.audit_summary.title': [{ key: 'notif.audit_summary.titleCount', needs: ['count'] }],
};

/** Title and body of a notification in the current language (the server never sends sentences, plan API-17). */
export function notificationText(n: AppNotification, t: TFn, hasT: (k: string) => boolean): { title: string; body: string | null } {
  const raw = n.params;
  const params: Record<string, unknown> = { ...raw };
  if (typeof raw.tool === 'string' && (TOOLS as readonly string[]).includes(raw.tool)) params.tool = sourceInfo(SOURCE_OF_TOOL[raw.tool as Tool]).label;
  if (typeof raw.reasonCategory === 'string') params.reasonCategory = t('reasonCat.' + raw.reasonCategory);
  if (typeof raw.role === 'string' && hasT('role.' + raw.role)) params.role = t('role.' + raw.role);
  const pick = (base: string): string => {
    const v = (NOTIF_VARIANTS[base] ?? []).find((x) => x.needs.every((k) => raw[k] != null && raw[k] !== ''));
    return v ? v.key : base;
  };
  const titleKey = pick(`notif.${n.kind}.title`);
  const bodyKey = pick(`notif.${n.kind}.body`);
  return { title: t(titleKey, params), body: hasT(bodyKey) ? t(bodyKey, params) : null };
}

/** Bell (074): rings once when the unread count grows (not on first paint, not with reduced motion). */
export function BellMenu() {
  const t = useT();
  const hasT = useHasT();
  const fmt = useFmt();
  const { me } = useAuth();
  const q = useNotifications(!!me);
  const mark = useMarkNotifications();
  const list = (q.data?.items ?? []).slice().sort((a, b) => b.at - a.at);
  const unread = q.data ? Math.max(q.data.unreadCount, list.filter((n) => !n.read).length) : 0;
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mb = useMenuButton(menuRef, btnRef);
  const seen = useRef<number | null>(null);
  const [ringing, setRinging] = useState(false);
  useEffect(() => {
    const before = seen.current;
    seen.current = unread;
    if (before == null || unread <= before) return;
    announce(t('bell.newLive', { n: unread - before }));
    if (reduceMotion()) return;
    setRinging(true);
    const tm = setTimeout(() => setRinging(false), BELL_RING_MS + 100);
    return () => clearTimeout(tm);
  }, [unread, t]);
  const setRead = (ids: string[] | 'all') => {
    if (ids !== 'all' && !ids.length) return;
    mark.mutate(ids);
  };
  const label = unread ? t('header.bellUnread', { n: unread }) : t('header.bell');
  return (
    <div className="hdr-pop">
      <button ref={btnRef} className={cls('icon-btn bell-btn', ringing && 'is-ringing')} type="button" aria-controls="bell-menu" aria-label={label} data-tip={label} data-unread={unread} {...mb.buttonProps}>
        <Icon name="bell" />
        {unread ? (
          <em className="bell-btn__badge" aria-hidden="true">
            {unread}
          </em>
        ) : null}
      </button>
      <div ref={menuRef} className="menu bell-menu" id="bell-menu" role="region" aria-label={t('bell.title')} {...mb.menuProps}>
        <div className="menu__head menu__head--row">
          <b>{t('bell.title')}</b>
          {unread ? (
            <button
              className="link-btn"
              type="button"
              onClick={() => {
                setRead('all');
                toast(t('bell.allRead'), 'ok');
              }}
            >
              {t('bell.readAll')}
            </button>
          ) : null}
        </div>
        {list.length ? (
          <ul className="notif-list">
            {list.map((n) => {
              const text = notificationText(n, t, hasT);
              return (
                <li key={n.id}>
                  <Link className={cls('notif', !n.read && 'is-unread')} to={appPath(n.href) ?? '/home'} onClick={() => (n.read ? undefined : setRead([n.id]))}>
                    <span className={`notif__ic notif__ic--${n.tone}`}>
                      <Icon name={NOTIF_ICON[n.kind] ?? 'info'} />
                    </span>
                    <span className="notif__body">
                      <b>{text.title}</b>
                      {text.body ? <span>{text.body}</span> : null}
                      <time dateTime={new Date(n.at).toISOString()}>{fmt.rel(n.at)}</time>
                    </span>
                    {!n.read ? <i className="notif__dot" aria-label={t('bell.unread')} /> : null}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="menu__empty">{t('bell.empty')}</p>
        )}
      </div>
    </div>
  );
}
